import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import { Button, Col, Row, Spinner } from "reactstrap";
import * as Yup from "yup";
import { getCategoryById, updateCategory } from "../../../../../api/categoryAPIs";
import { Block, BlockBetween, BlockHead, BlockHeadContent, BlockTitle, PreviewCard } from "../../../../../components/Component";
import Content from "../../../../../layout/content/Content";

const EditCategoryPage = () => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const { id } = useParams();

    const navigate = useNavigate();

    const validationSchema = Yup.object({
        name: Yup.string().required("Category name is required"),
        description: Yup.string().required("Description is required"),
        images: Yup.array().test(
            "fileType",
            "Only the following formats are accepted: .jpeg, .png, .jpg",
            (files) =>
                files?.length === 0 || files.every((file) =>
                    ["image/jpeg", "image/png", "image/jpg"].includes(file.type)
                )
        ),
    });

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                setLoading(true);
                const response = await getCategoryById({ id });
                if (response.error) {
                    toast.error(response.error, { position: "top-center" });
                } else {
                    const category = response.data.data;
                    setInitialValues({
                        name: category.name,
                        description: category.description,
                        images: [], // Initially empty to handle only new uploads
                    });
                    setFiles(category.images.map((img) => ({ preview: img, name: img })));
                }
            } catch (error) {
                toast.error("Failed to load category data.", { position: "top-center" });
            } finally {
                setLoading(false);
            }
        };

        fetchCategory();
    }, [id]);

    const handleDropChange = (acceptedFiles, setFieldValue) => {
        setFiles(
            acceptedFiles.map((file) =>
                Object.assign(file, { preview: URL.createObjectURL(file) })
            )
        );
        setFieldValue("images", acceptedFiles);
    };

    const handleSubmit = async (values, { resetForm }) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append(
                "data",
                JSON.stringify({ name: values.name, description: values.description })
            );

            // Append new files only if present
            if (values.images && values.images.length > 0) {
                values.images.forEach((image) => {
                    if (image instanceof File) {
                        formData.append("images", image);
                    }
                });
            }

            const response = await updateCategory(id, { formData });

            if (response.error) {
                toast.error(response.error, { position: "top-center" });
            } else {
                toast.success("Category updated successfully", { position: "top-center" });
                resetForm();
                setFiles([]);
                navigate("/category");
            }
        } catch (error) {
            toast.error("Something went wrong. Please try again later.", { position: "top-center" });
        } finally {
            setLoading(false);
        }
    };

    if (loading || !initialValues) {
        return <Spinner className="d-flex justify-content-center mt-5" />;
    }

    return (
        <Content className="container mx-auto p-4">
            <Block>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Edit Category
                            </BlockTitle>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>

                <PreviewCard>
                    <div className="rounded-lg p-6">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize
                        >
                            {({ setFieldValue }) => (
                                <Form>
                                    <Row className="g-3" xs={1}>
                                        <Col size="12">
                                            <div className="form-group">
                                                <label className="form-label" htmlFor="name">
                                                    Category Name
                                                </label>
                                                <Field
                                                    type="text"
                                                    className="form-control"
                                                    name="name"
                                                    placeholder="Enter category name"
                                                />
                                                <ErrorMessage name="name" component="span" className="invalid" />
                                            </div>
                                        </Col>

                                        <Col size="12">
                                            <div className="form-group">
                                                <label className="form-label" htmlFor="description">
                                                    Description
                                                </label>
                                                <Field
                                                    as="textarea"
                                                    className="form-control"
                                                    name="description"
                                                    placeholder="Enter category description"
                                                />
                                                <ErrorMessage name="description" component="span" className="invalid" />
                                            </div>
                                        </Col>

                                        <Col size="6">
                                            <div className="form-group">
                                                <label className="form-label">Category Image</label>
                                                <Dropzone
                                                    onDrop={(acceptedFiles) =>
                                                        handleDropChange(acceptedFiles, setFieldValue)
                                                    }
                                                >
                                                    {({ getRootProps, getInputProps }) => (
                                                        <section>
                                                            <div
                                                                {...getRootProps()}
                                                                className="dropzone upload-zone small bg-lighter my-2 dz-clickable"
                                                            >
                                                                <input {...getInputProps()} />
                                                                {files.length === 0 && (
                                                                    <p>
                                                                        Drag 'n' drop some files here, or click to select files
                                                                    </p>
                                                                )}
                                                                {files.map((file) => (
                                                                    <div
                                                                        key={file.name}
                                                                        className="dz-preview dz-processing dz-image-preview dz-error dz-complete"
                                                                    >
                                                                        <div className="dz-image">
                                                                            <img src={file.preview} alt="preview" />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </section>
                                                    )}
                                                </Dropzone>
                                                <ErrorMessage name="images" component="span" className="invalid" />
                                            </div>
                                        </Col>

                                        <Col size="12">
                                            <div className="d-flex justify-content-end">
                                                <Button color="primary" type="submit" disabled={loading}>
                                                    {loading ? <Spinner size="sm" /> : "Update Category"}
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </PreviewCard>
            </Block>
        </Content>
    );
};

export default EditCategoryPage;